/**
 * The function `PaperCard` returns a JSX element representing a card component displaying details of a
 * paper with options for deletion.
 * @param paper - The `PaperCard` function takes a `paper` object as a parameter. The `paper` object
 * likely contains information about a paper document, such as its `id`, `date_uploaded`, `name`, and
 * `description`. The function then renders a card component displaying details of the paper, including
 * @returns The PaperCard function returns a JSX element that consists of a Stack component containing
 * a Card component displaying details of a paper, along with a FadeMenu component for additional
 * options like deleting the paper.
 */

import React from "react";
import FadeMenu from "../FadeMenu";
import { useNavigate } from "react-router-dom";
import CustomSkeleton from "../CustomSkeleton";
import "./PaperCard.module.css";
import docxIcon from "../../assets/docx.png";
import Pagination from "../ui/Pagination";

/** 
  *handleMenuItemClick is a function that takes a two parameter,`item` and `value` ,
    which is the id of the paper value is normally the id of the paper
**/

// used in 
// user profile component
// previous uploads page
// project papers tab

function PaperList({
  papers,
  handleMenuItemClick,
  projectId,
  initiativeId,
  loading,
  pagination={}, // for pagination state
  setPagination, // function to update pagination state
}) {
  if (loading) {
    // Display a skeleton loader while loading as same size of the card
    return (
      <div className="d-flex flex-column gap-3 p-3">
        <CustomSkeleton height={50} count={3} />;
      </div>
    );
  }

  if (!papers || papers.length === 0) {
    return <div className="text-center p-4 p5">No papers found</div>;
  }

  console.log("Papers:", papers);

  return (
    <div className="d-flex flex-column gap-3 p-3">
      {papers?.map((paper) => (
        <PaperCard paper={paper} handleMenuItemClick={handleMenuItemClick} />
      ))}
      {pagination && pagination.totalItems > 0 &&
        // Render pagination only if there are items to paginate
        <Pagination
          currentPage={pagination.currentPage} // Replace with actual current page state
          totalItems={pagination.totalItems} // Replace with actual total items count
          pageSize={pagination.pageSize || 10} // Replace with actual page size
          onPageChange={
            (page) => {
              setPagination((prev) => ({
                ...prev,
                currentPage: page,
              }));
            }
          }
        />
      }
    </div>
  );
}

export function PaperCard({ paper, handleMenuItemClick }) {
  const navigate = useNavigate();

  // Set the navigation URL based on conditions
  let navigationUrl = "";

  if (paper && paper.id) {
    if (paper.is_reviewed) {
      navigationUrl = `/insights-postchecks/${paper.id}`;
    } else if (paper.journal && paper.journal.id) {
      navigationUrl = `/journals/${paper.journal.id}/preview/${paper.id}`;
    } else {
      // Handle missing journal gracefully
      navigationUrl = "#"; // or show an error message / fallback route
    }
  }

  // Handle the card click event
  const handleCardClick = () => {
    console.log("Card clicked:", paper.id, navigationUrl);

    if (navigationUrl) navigate(navigationUrl);
  };

  return (
    <div
      key={paper.id}
      style={{ cursor: "pointer" }}
      className={`w-100 d-flex justify-content-between rounded-4 p-3 secondary-box`}
    >
      <img
        src={docxIcon}
        alt="DOCX"
        width={28}
        height={30}
        className="m-2 me-3"
      />
      <div onClick={handleCardClick} className={`border-0 rounded-0 w-100`}>
        <div>
          <p className="p3 fw-medium mb-0 text-break text-capitalize">
            {paper.name.replace(".pdf", "")}
          </p>
          <div className="p5 text-secondary">{paper.description}</div>
          {paper.date_uploaded && (
            <div className="my-2">
              {`${new Date(paper.date_uploaded).toLocaleString()} `}
              {/* {`${paper.date_uploaded} ${paper.status} `} */}
              {/* to ${paper.owner.name}*/}
            </div>
          )}
          <div className="p5 text-secondary">
            {paper.project?.name && (
              <>
                <span className="fw-bold pe-1">Project:</span> {paper.project.name}
              </>
            )}
            {paper.workstream?.name && (
              <>
                {" "} | <span className="fw-bold pe-1">Workstream:</span>{" "}
                 {paper.workstream?.name}
              </>
            )}
          </div>
        </div>
      </div>
      <FadeMenu
        buttonLabel="..."
        menuItems={["Delete"]}
        onMenuItemClick={handleMenuItemClick}
        value={paper.id}
      />
    </div>
  );
}

export default PaperList;
